import React, { useState } from "react";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
} from "@mui/material";
import Input from "../../../../../../components/Input";
import Text from "../../../../../../components/Text";
import Flex from "../../../../../../components/Flex";
import AsyncSelect from "../../../../../../components/AsyncSelect";
import CustomSelect from "../../../../../../components/CustomSelect";
import CustomDatePicker from "../../../../../../components/CustomDatePicker";
import MaskInput from "../../../../../../components/MaskInput";
import StyledButtonV2 from "../../../../../../components/app/StyledButtonV2";
import { COUNTRIES_LIST, STATE_LIST } from "../../../../../../constants";
import { RELATIONSHIP_WITH_PATIENT } from "../../../../../../constants/provider/add-new-patient";
import { PatientInsuranceSubscriberGenderEnum } from "../../../../../../services";
import { useInsurancePayerList } from "../../../../../../queries/use-insurance-payer";
import { useGetAllInsurancePlan } from "../../../../../../queries/insurance/use-get-all-insurance-plan";
import useDisclosure from "../../../../../../hooks/use-disclosure";
import AddCardModal from "../../../patient/patient-list/patient-chart-new/financial/AddCardModal";
import { getXtenantId } from "../../../../../../utils/utils";
import { getPublicRealm } from "../../../../../../layout/app/patient-portal/public-view/dob-validation/useValidateDob";
import { Option } from "../../../../../../constants/common";
import {
  INITIAL_INTAKE_INSURANCE,
  INSURANCE_ANSWER,
  IntakeInsurance,
  MAX_INTAKE_INSURANCE_COUNT,
  ORDER_OF_BENEFITS_OPTIONS,
} from "../new-intake-form-contant/intake-constant";

const SELF_PAY_CARD_COPY = {
  title: "Add Payment Card",
  description:
    "You have indicated that you do not have insurance. Please add a payment card to securely save it for future payments and billing.",
  saveLabel: "Save Card",
  success: "Payment card added successfully.",
  error:
    "Unable to save your payment card. Please verify your card details and try again.",
};

interface ClientInsuranceFormProps {
  values: any;
  setFieldValue: (field: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  touched: any;
  errors: any;
  viewOnly?: boolean;
  // Needed to attach the Stripe payment method to the right patient when the
  // self-pay ("No insurance") card modal is used.
  patientId?: string;
  // The open intake-form link renders this form unauthenticated.
  isPublic?: boolean;
}

interface InsurancePolicyCardProps {
  /** Position in `patientInsurances` — drives the field paths and the title. */
  index: number;
  insurance?: IntakeInsurance;
  setFieldValue: (field: string, value: any) => void;
  handleChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  handleBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  viewOnly?: boolean;
  tenantId?: string;
  /** Benefit orders still free — the ones the other policies hold are out. */
  orderOfBenefitsOptions: Option[];
  /** Omitted on the first policy, which cannot be removed. */
  onRemove?: () => void;
}

/**
 * One insurance policy. Each card owns its payer and plan lists so the plans
 * offered follow that policy's payer rather than the first policy's.
 */
const InsurancePolicyCard = ({
  index,
  insurance,
  setFieldValue,
  handleChange,
  handleBlur,
  viewOnly,
  tenantId,
  orderOfBenefitsOptions,
  onRemove,
}: InsurancePolicyCardProps) => {
  const [searchPayerKey, setSearchPayerKey] = useState<string>("");
  const [searchPlanKey, setSearchPlanKey] = useState<string>("");

  const { data: insurancePayerList, isLoading: insurancePayerListLoading } =
    useInsurancePayerList({
      searchKey: searchPayerKey,
      xTENANTID: tenantId,
    });

  const { data: insurancePlanList, isLoading: insurancePlanListLoading } =
    useGetAllInsurancePlan({
      status: true,
      pagination: { page: 0, pageSize: 20 },
      payerId: insurance?.insurancePayer?.payerId?.value ?? "",
      searchKey: searchPlanKey,
      xTENANTID: tenantId,
    });

  return (
    <Box
      sx={{
        border: "1px solid #e0e0e0",
        borderRadius: "4px",
        p: 2,
        mb: 2,
      }}
    >
      <Flex justifyContent="space-between" alignItems="center" mb={1}>
        <Text variant="body1" fontWeight="500">
          {`Insurance ${index + 1}`}
        </Text>
        {!viewOnly && onRemove && (
          <StyledButtonV2
            variant="text"
            color="primary"
            label="Remove"
            sIcon={<HighlightOffOutlinedIcon />}
            onClick={onRemove}
          />
        )}
      </Flex>

      <Grid container spacing={2}>
        {/* Policy Information Header */}
        <Grid size={12}>
          <Text variant="body1" fontWeight="500">
            Policy Information
          </Text>
        </Grid>

        {/* Payer Name */}
        <Grid size={12}>
          <AsyncSelect
            disabled={viewOnly}
            loading={insurancePayerListLoading}
            value={insurance?.insurancePayer?.payerId}
            onChange={(opt) => {
              setFieldValue(
                `patientInsurances[${index}].insurancePayer.payerId`,
                opt
              );
              setFieldValue(`patientInsurances[${index}].planId`, undefined);
              setFieldValue(`patientInsurances[${index}].planName`, undefined);
              setFieldValue(`patientInsurances[${index}].planType`, undefined);
              setFieldValue(
                `patientInsurances[${index}].coverageType`,
                undefined
              );
              setSearchPlanKey(opt?.value ?? "");
            }}
            onSearch={setSearchPayerKey}
            options={insurancePayerList?.data?.content?.map((it) => ({
              value: it.payerId,
              label: it.payerName,
            }))}
            label="Payer Name"
          />
        </Grid>

        {/* Plan Name */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <AsyncSelect
            disabled={viewOnly}
            loading={insurancePlanListLoading}
            value={insurance?.planId}
            onChange={(opt) => {
              setFieldValue(`patientInsurances[${index}].planId`, opt);
              setFieldValue(`patientInsurances[${index}].planName`, opt);

              const selectedInsurance = insurancePlanList?.data?.content?.find(
                (it) => it.uuid === opt?.value
              );
              setFieldValue(
                `patientInsurances[${index}].planType`,
                selectedInsurance?.planType ?? undefined
              );
              setFieldValue(
                `patientInsurances[${index}].coverageType`,
                selectedInsurance?.coverageType ?? undefined
              );
            }}
            onSearch={setSearchPlanKey}
            options={insurancePlanList?.data?.content?.map((it) => ({
              value: it.uuid,
              label: it.planName ?? "",
            }))}
            label="Plan Name"
          />
        </Grid>

        {/* Plan Type */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="Plan Type"
            textFieldProps={{
              name: `patientInsurances[${index}].planType`,
              value: insurance?.planType?.replaceAll("_", " ") ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: true,
            }}
          />
        </Grid>

        {/* Coverage Type */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="Coverage Type"
            textFieldProps={{
              name: `patientInsurances[${index}].coverageType`,
              value: insurance?.coverageType ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: true,
            }}
          />
        </Grid>

        {/* Order of Benefits */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <CustomSelect
            disableSortOptions
            label="Order of Benefits"
            value={insurance?.orderOfBenefits}
            options={orderOfBenefitsOptions}
            onChange={(value) =>
              setFieldValue(
                `patientInsurances[${index}].orderOfBenefits`,
                value
              )
            }
            readOnly={viewOnly}
          />
        </Grid>

        {/* Insurance ID */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="Insurance ID"
            textFieldProps={{
              name: `patientInsurances[${index}].insuranceId`,
              value: insurance?.insuranceId ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>

        {/* Member ID */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="Member ID"
            textFieldProps={{
              name: `patientInsurances[${index}].memberId`,
              value: insurance?.memberId ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>

        {/* Group ID */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="Group ID"
            textFieldProps={{
              name: `patientInsurances[${index}].groupId`,
              value: insurance?.groupId ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>

        {/* Effective Start Date */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <CustomDatePicker
            label="Effective Start Date"
            disabled={viewOnly}
            value={insurance?.effectiveStartDate}
            onChange={(value) =>
              setFieldValue(
                `patientInsurances[${index}].effectiveStartDate`,
                value
              )
            }
          />
        </Grid>

        {/* Effective End Date */}
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <CustomDatePicker
            label="Effective End Date"
            disabled={viewOnly}
            value={insurance?.effectiveEndDate}
            onChange={(value) =>
              setFieldValue(
                `patientInsurances[${index}].effectiveEndDate`,
                value
              )
            }
          />
        </Grid>

        {/* Subscriber Information */}
        <Grid sx={{ mt: 1 }} size={12}>
          <Text variant="body1" fontWeight="500">
            Subscriber Information
          </Text>
        </Grid>

        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <CustomSelect
            disableSortOptions
            label="Relationship to Insured"
            value={insurance?.insuredRelationshipWithPatient}
            options={RELATIONSHIP_WITH_PATIENT}
            onChange={(value) =>
              setFieldValue(
                `patientInsurances[${index}].insuredRelationshipWithPatient`,
                value
              )
            }
            readOnly={viewOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="First Name"
            textFieldProps={{
              name: `patientInsurances[${index}].subscriberFirstName`,
              value: insurance?.subscriberFirstName ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="Middle Name"
            textFieldProps={{
              name: `patientInsurances[${index}].subscriberMiddleName`,
              value: insurance?.subscriberMiddleName ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="Last Name"
            textFieldProps={{
              name: `patientInsurances[${index}].subscriberLastName`,
              value: insurance?.subscriberLastName ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <CustomDatePicker
            label="Date of Birth"
            disabled={viewOnly}
            value={insurance?.subscriberBirthDate}
            onChange={(value) =>
              setFieldValue(
                `patientInsurances[${index}].subscriberBirthDate`,
                value
              )
            }
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <CustomSelect
            label="Gender"
            value={insurance?.subscriberGender}
            options={Object.entries(PatientInsuranceSubscriberGenderEnum).map(
              ([key, value]) => ({ value, label: key })
            )}
            onChange={(value) =>
              setFieldValue(
                `patientInsurances[${index}].subscriberGender`,
                value
              )
            }
            readOnly={viewOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="Social Security Number"
            textFieldProps={{
              name: `patientInsurances[${index}].subscriberSsn`,
              value: insurance?.subscriberSsn ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <MaskInput
            mask={"(999) 999-9999"}
            label="Mobile Number"
            disabled={viewOnly}
            name={`patientInsurances[${index}].subscriberMobileNumber`}
            onBlur={handleBlur}
            value={insurance?.subscriberMobileNumber ?? ""}
            onChange={(e) =>
              setFieldValue(
                `patientInsurances[${index}].subscriberMobileNumber`,
                e.target.value
              )
            }
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <Input
            label="Address Line 1"
            textFieldProps={{
              name: `patientInsurances[${index}].subscriberAddress.line1`,
              value: insurance?.subscriberAddress?.line1 ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <Input
            label="Address Line 2"
            textFieldProps={{
              name: `patientInsurances[${index}].subscriberAddress.line2`,
              value: insurance?.subscriberAddress?.line2 ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="City"
            textFieldProps={{
              name: `patientInsurances[${index}].subscriberAddress.city`,
              value: insurance?.subscriberAddress?.city ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <CustomSelect
            label="State"
            value={insurance?.subscriberAddress?.state}
            options={STATE_LIST}
            onChange={(value) =>
              setFieldValue(
                `patientInsurances[${index}].subscriberAddress.state`,
                value
              )
            }
            readOnly={viewOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <CustomSelect
            label="Country"
            disableSortOptions
            value={insurance?.subscriberAddress?.country}
            options={COUNTRIES_LIST}
            onChange={(value) =>
              setFieldValue(
                `patientInsurances[${index}].subscriberAddress.country`,
                value
              )
            }
            readOnly={viewOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="ZIP Code"
            textFieldProps={{
              name: `patientInsurances[${index}].subscriberAddress.zipcode`,
              value: insurance?.subscriberAddress?.zipcode ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>

        {/* Employer Information */}
        <Grid sx={{ mt: 1 }} size={12}>
          <Text variant="body1" fontWeight="500">
            Employer Information
          </Text>
        </Grid>

        <Grid size={12}>
          <Input
            label="Employer Name"
            textFieldProps={{
              name: `patientInsurances[${index}].employerName`,
              value: insurance?.employerName ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <Input
            label="Address Line 1"
            textFieldProps={{
              name: `patientInsurances[${index}].employerAddress.line1`,
              value: insurance?.employerAddress?.line1 ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
          }}
        >
          <Input
            label="Address Line 2"
            textFieldProps={{
              name: `patientInsurances[${index}].employerAddress.line2`,
              value: insurance?.employerAddress?.line2 ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="City"
            textFieldProps={{
              name: `patientInsurances[${index}].employerAddress.city`,
              value: insurance?.employerAddress?.city ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <CustomSelect
            label="State"
            value={insurance?.employerAddress?.state}
            options={STATE_LIST}
            onChange={(value) =>
              setFieldValue(
                `patientInsurances[${index}].employerAddress.state`,
                value
              )
            }
            readOnly={viewOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <CustomSelect
            label="Country"
            disableSortOptions
            value={insurance?.employerAddress?.country}
            options={COUNTRIES_LIST}
            onChange={(value) =>
              setFieldValue(
                `patientInsurances[${index}].employerAddress.country`,
                value
              )
            }
            readOnly={viewOnly}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            sm: 6,
            lg: 3,
          }}
        >
          <Input
            label="ZIP Code"
            textFieldProps={{
              name: `patientInsurances[${index}].employerAddress.zipcode`,
              value: insurance?.employerAddress?.zipcode ?? "",
              onChange: handleChange,
              onBlur: handleBlur,
              disabled: viewOnly,
            }}
          />
        </Grid>
      </Grid>
    </Box>
  );
};

const ClientInsuranceForm = ({
  values,
  setFieldValue,
  handleChange,
  handleBlur,
  viewOnly,
  patientId,
  isPublic,
}: ClientInsuranceFormProps) => {
  const tenantId = getXtenantId() ?? getPublicRealm() ?? undefined;

  // Forms saved before the answer was persisted carry no flag, and insurance
  // stays the default selection for them.
  const hasInsurance: boolean = values?.hasInsurance ?? true;
  const addCardDisclosure = useDisclosure(false);

  // Forms saved before multiple policies were supported carry no array.
  const policies: IntakeInsurance[] = values?.patientInsurances?.length
    ? values.patientInsurances
    : [INITIAL_INTAKE_INSURANCE];

  // A benefit order belongs to one policy, so each card is offered the orders
  // the others have not taken plus the one it already holds.
  const orderOfBenefitsOptionsFor = (index: number) => {
    const taken = new Set<string>(
      policies.map((policy, policyIndex) =>
        policyIndex === index ? "" : (policy?.orderOfBenefits ?? "")
      )
    );
    return ORDER_OF_BENEFITS_OPTIONS.filter(
      (option) => !taken.has(option.value)
    );
  };

  return (
    <Box>
      {/* Insurance question drives the rest of the form, so it comes first. */}
      <Flex gap={2} alignItems={"center"} mb={1}>
        <FormLabel disabled={viewOnly}>
          <Text>Do you have Insurance?</Text>
        </FormLabel>
        <RadioGroup
          row
          value={hasInsurance ? INSURANCE_ANSWER.YES : INSURANCE_ANSWER.NO}
          onChange={(_, value) => {
            const answeredYes = value === INSURANCE_ANSWER.YES;
            setFieldValue("hasInsurance", answeredYes);
            // A self-pay patient declares no policy, so nothing from the
            // insurance block is carried into the saved form content.
            if (!answeredYes) {
              setFieldValue("patientInsurances", []);
              setFieldValue("agreeToReleaseTerms", false);
              setFieldValue("assignBenefitsToPractice", false);
              setFieldValue("signedByName", "");
            }
          }}
        >
          <FormControlLabel
            disabled={viewOnly}
            value={INSURANCE_ANSWER.YES}
            control={<Radio />}
            label={<Text>Yes</Text>}
          />
          <FormControlLabel
            disabled={viewOnly}
            value={INSURANCE_ANSWER.NO}
            control={
              <Radio
                // onClick (not onChange) so picking "No" again re-opens the
                // modal after it was cancelled — nothing else is rendered on
                // the page for the self-pay flow.
                onClick={() => {
                  if (!viewOnly) addCardDisclosure.open();
                }}
              />
            }
            label={<Text>No</Text>}
          />
        </RadioGroup>
      </Flex>

      {addCardDisclosure.opened && (
        <AddCardModal
          disclosureHost={addCardDisclosure}
          patientId={patientId}
          isPublic={isPublic}
          dialogTitle={SELF_PAY_CARD_COPY.title}
          description={SELF_PAY_CARD_COPY.description}
          saveLabel={SELF_PAY_CARD_COPY.saveLabel}
          successMessage={SELF_PAY_CARD_COPY.success}
          errorMessage={SELF_PAY_CARD_COPY.error}
        />
      )}

      {hasInsurance && (
        <>
          <Text variant="subtitle1" fontWeight="500" mb={1}>
            Insurance Policies
          </Text>

          {policies.map((insurance, index) => (
            <InsurancePolicyCard
              key={index}
              index={index}
              insurance={insurance}
              setFieldValue={setFieldValue}
              handleChange={handleChange}
              handleBlur={handleBlur}
              viewOnly={viewOnly}
              tenantId={tenantId}
              orderOfBenefitsOptions={orderOfBenefitsOptionsFor(index)}
              onRemove={
                index === 0
                  ? undefined
                  : () =>
                      setFieldValue(
                        "patientInsurances",
                        policies.filter((_, i) => i !== index)
                      )
              }
            />
          ))}

          {!viewOnly && policies.length < MAX_INTAKE_INSURANCE_COUNT && (
            <StyledButtonV2
              shouldAddIcon
              variant="text"
              label="Add Another Insurance"
              onClick={() =>
                setFieldValue("patientInsurances", [
                  ...policies,
                  INITIAL_INTAKE_INSURANCE,
                ])
              }
            />
          )}

          {/* The release terms cover every policy above, so they are
              acknowledged once instead of per policy. */}
          <Grid container spacing={2}>
            <Grid sx={{ mt: 1 }} size={12}>
              <Text variant="body1" fontWeight="500" mb={1}>
                Release of Information & Assignment of Benefits
              </Text>

              <FormControlLabel
                disabled={viewOnly || values?.noInsuranceBenefits}
                control={
                  <Checkbox
                    disabled={viewOnly || values?.noInsuranceBenefits}
                    checked={values?.agreeToReleaseTerms || false}
                    onChange={(e) =>
                      setFieldValue("agreeToReleaseTerms", e.target.checked)
                    }
                    name="agreeToReleaseTerms"
                  />
                }
                label="I understand and agree to the following:"
              />

              <Box
                sx={{
                  pl: 3,
                  mt: 1,
                  color: "text.secondary",
                  fontSize: "0.85rem",
                }}
              >
                <ul>
                  <li>
                    I authorize the release of information from my medical
                    record to the insurance company or other third-party payer
                    named above...
                  </li>
                  <li>
                    If the practice has already shared information with the
                    insurance company... it is too late to prevent that
                    information from being shared.
                  </li>
                  <li>
                    This authorization is necessary for the practice to
                    determine eligibility for treatments or benefits...
                  </li>
                  <li>
                    This authorization shall be effective for 1 year from the
                    date of my signature...
                  </li>
                  <li>
                    If you are using Medicare benefits, you also agree to the
                    following: I request that payment of authorized Medicare
                    benefits be made either to me or on my behalf...
                  </li>
                </ul>
              </Box>
            </Grid>

            <Grid size={12}>
              <FormControlLabel
                disabled={viewOnly || values?.noInsuranceBenefits}
                control={
                  <Checkbox
                    disabled={viewOnly || values?.noInsuranceBenefits}
                    checked={values?.assignBenefitsToPractice || false}
                    onChange={(e) =>
                      setFieldValue(
                        "assignBenefitsToPractice",
                        e.target.checked
                      )
                    }
                    name="assignBenefitsToPractice"
                  />
                }
                label="In consideration of the services provided to me, I assign all benefits to the practice, if accepted, and authorize this insurance company to make payments directly to the practice and its affiliates on my behalf."
              />
            </Grid>
          </Grid>

          {/* Acknowledgment Signature block — it acknowledges the insurance
              declaration above, so it is not shown in the self-pay flow. */}
          <Box sx={{ mt: 2, pt: 2, borderTop: "1px dashed #ccc" }}>
            <Text variant="body1" fontWeight="500" mb={1}>
              Acknowledgment
            </Text>
            <Text variant="body2" color="textSecondary" mb={2}>
              I have read and understood the contents of this form. All
              information entered by me is true and accurate, and I have only
              entered information about myself or an individual I am authorized
              to act on behalf of, such as my child. I consent to use electronic
              signatures, and my signature below is the same as a handwritten
              signature for the purposes of validity, enforceability, and
              admissibility.
            </Text>

            <Grid container spacing={2}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6,
                }}
              >
                <Input
                  label="Signed By:"
                  textFieldProps={{
                    name: "signedByName",
                    value: values?.signedByName || "",
                    onChange: handleChange,
                    onBlur: handleBlur,
                    placeholder: "Enter Your Legal Name",
                    disabled: viewOnly,
                  }}
                />
              </Grid>
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
};

export default ClientInsuranceForm;
